import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import Select from "react-select";
import '../CSS/Global.css';
import { useSidebar } from '../SidebarContext';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const statusOptions = ['Accepted', 'Rejected', 'Published', 'Under Review'];

const getStatusClass = (status) => {
    switch (status) {
        case 'Accepted':
            return 'status-accepted';
        case 'Rejected':
            return 'status-rejected';
        case 'Published':
            return 'status-published';
        case 'Under Review':
            return 'status-under-review';
        default:
            return '';
    }
};

const Articles = () => {
    const { isSidebarOpen } = useSidebar();
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedArticle, setSelectedArticle] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const cardsPerPage = 4;
    const [searchQuery, setSearchQuery] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [pendingStatus, setPendingStatus] = useState('');
    const [pendingStatusIndex, setPendingStatusIndex] = useState(null);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [fileToDeleteIndex, setFileToDeleteIndex] = useState(null);
    const [paymentFilter, setPaymentFilter] = useState("All");
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isDownloadConfirmOpen, setIsDownloadConfirmOpen] = useState(false);
    const [pendingDownloadData, setPendingDownloadData] = useState(null);




    const [articleStatuses, setArticleStatuses] = useState(cards.map(() => 'Under Review'));


    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handleFileChange = (e, index) => {
        const file = e.target.files[0];
        if (file) {
            setUploadedFiles(prev => ({
                ...prev,
                [index]: { name: file.name, file }
            }));
            toast.success(`File "${file.name}" uploaded successfully!`);  // Toast message after file upload
        }
    };


    const handleFileDelete = (index) => {
        setUploadedFiles(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
        });

        // Clear the input value manually
        const input = document.getElementById(`upload-input-${index}`);
        if (input) input.value = '';
    };


    const downloadFile = (fileData) => {
        const url = URL.createObjectURL(fileData.file);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileData.name;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Example options
    const [volumeOptions, setVolumeOptions] = useState([]);
    const [issueOptions, setIssueOptions] = useState([]);

    // const issueOptions = [
    //     { value: "Issue-1", label: "Issue 1" },
    //     { value: "Issue-2", label: "Issue 2" },
    //     { value: "Issue-3", label: "Issue 3" },
    // ];


    const filteredCards = cards.filter(card => {
        const query = searchQuery.toLowerCase();
        const payStatus = (card.manuscriptDetails.pay_status || "").toLowerCase();

        const matchesSearch = (
            (card.manuscriptDetails.title || card.manuscriptDetails.articleTitle || '').toLowerCase().includes(query) ||
            (card.manuscriptDetails.author || card.manuscriptDetails.name || '').toLowerCase().includes(query) ||
            (card.manuscriptDetails.category || card.manuscriptDetails.articleCategory || '').toLowerCase().includes(query) ||
            (card.manuscriptDetails.referenceNumber || '').toLowerCase().includes(query) ||
            (card.manuscriptDetails.paymentStatus || '').toLowerCase().includes(query)
        );

        const matchesPayment =
            paymentFilter === "All" ||
            (paymentFilter === "Paid" && payStatus === "paid") ||
            (paymentFilter === "Unpaid" && payStatus === "unpaid");

        const createdAt = new Date(card.manuscriptDetails.createdAt);
        const createdDateStr = createdAt.toISOString().slice(0, 10); // format: yyyy-mm-dd

        const start = startDate || '';
        const end = endDate || '';

        const matchesDate = (() => {
            if (start && end && start === end) {
                return createdDateStr === start;
            } else if (start && end) {
                return createdDateStr >= start && createdDateStr <= end;
            } else if (start) {
                return createdDateStr >= start;
            } else if (end) {
                return createdDateStr <= end;
            }
            return true;
        })();

        return matchesSearch && matchesPayment && matchesDate;
    });



    const totalPages = Math.ceil(filteredCards.length / cardsPerPage);
    const indexOfLastCard = currentPage * cardsPerPage;
    const indexOfFirstCard = indexOfLastCard - cardsPerPage;
    const currentCards = filteredCards.slice(indexOfFirstCard, indexOfLastCard);

    const paginate = (pageNumber) => {
        if (pageNumber >= 1 && pageNumber <= totalPages) {
            setCurrentPage(pageNumber);
        }
    };

    const handleEyeClick = (card) => {
        setSelectedArticle(card);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedArticle(null);
    };
    const handleStatusChange = (e, card) => {
        setPendingStatus(e.target.value);
        setSelectedArticle(card);  // Store the full card
        setIsConfirmModalOpen(true);
    };



    useEffect(() => {
        const fetchManuscripts = async () => {
            try {
                const response = await fetch(`https://releasesworld.100billionoms.com/processingfee/merged`);
                if (!response.ok) {
                    throw new Error("Failed to fetch manuscripts");
                }
                const data = await response.json();
                setCards(data.reverse());

            } catch (error) {
                console.error("Error fetching manuscripts:", error);
                toast.error("Failed to load manuscripts");
            } finally {
                setLoading(false);
            }
        };

        fetchManuscripts();
    }, []);


    const confirmStatusChange = async () => {
        const referenceNumber = selectedArticle?.manuscriptDetails?.referenceNumber;

        try {
            const response = await fetch(`https://releasesworld.100billionoms.com/manuscript/update-status/${referenceNumber}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ statusOptions: pendingStatus }),
            });

            const result = await response.json();

            if (response.ok) {
                const updated = cards.map((card) => {
                    if (card.manuscriptDetails.referenceNumber === referenceNumber) {
                        return {
                            ...card,
                            manuscriptDetails: {
                                ...card.manuscriptDetails,
                                statusOptions: pendingStatus
                            }
                        };
                    }
                    return card;
                });

                // If the status was changed from "Published", delete certificate if exists
                if (
                    selectedArticle.manuscriptDetails.statusOptions === "Published" &&
                    pendingStatus !== "Published" &&
                    selectedArticle.manuscriptDetails.certificate
                ) {
                    await fetch(`https://releasesworld.100billionoms.com/manuscript/delete-certificate/${referenceNumber}`, {
                        method: "DELETE",
                    });

                    updated.forEach((card) => {
                        if (card.manuscriptDetails.referenceNumber === referenceNumber) {
                            card.manuscriptDetails.certificate = null;
                        }
                    });
                }

                setCards(updated);
                toast.success(`Status updated to ${pendingStatus}`);
            } else {
                toast.error(result.message || "Failed to update status.");
            }
        } catch (err) {
            toast.error("Server error. Please try again.");
        } finally {
            setIsConfirmModalOpen(false);
            setPendingStatus('');
            setSelectedArticle(null);
        }
    };




    const cancelFileDelete = () => {
        setIsDeleteConfirmOpen(false);
        setFileToDeleteIndex(null);
    };

    const uploadCertificateToServer = async (index) => {
        const card = cards[index];
        const fileData = uploadedFiles[index]?.file;
        if (!fileData) return;

        setLoading(true); // Start loading

        const formData = new FormData();
        formData.append("certificate", fileData);

        try {
            const response = await fetch(`https://releasesworld.100billionoms.com/manuscript/upload-certificate/${card.referenceNumber}`, {
                method: "POST",
                body: formData,
            });

            const result = await response.json();

            if (response.ok) {
                toast.success("Certificate uploaded successfully");
                const updated = [...cards];
                updated[index] = {
                    ...updated[index],
                    certificate: result.file,
                    manuscriptDetails: {
                        ...updated[index].manuscriptDetails,
                        certificate: result.file
                    }
                };
                setCards(updated);
            }
            else {
                toast.error(result.message || "Upload failed.");
            }
        } catch (err) {
            toast.error("Server error during certificate upload.");
        } finally {
            setLoading(false); // End loading
        }
    };


    const confirmFileDelete = async () => {
        const referenceNumber = selectedArticle?.manuscriptDetails?.referenceNumber;

        setLoading(true); // Start loading

        try {
            const response = await fetch(`https://releasesworld.100billionoms.com/manuscript/delete-certificate/${referenceNumber}`, {
                method: "DELETE",
            });

            const result = await response.json();

            if (response.ok) {
                const updated = [...cards];
                updated[fileToDeleteIndex] = {
                    ...updated[fileToDeleteIndex],
                    certificate: null,
                    manuscriptDetails: {
                        ...updated[fileToDeleteIndex].manuscriptDetails,
                        certificate: null
                    }
                };
                setCards(updated);

                const uploadedCopy = { ...uploadedFiles };
                delete uploadedCopy[fileToDeleteIndex];
                setUploadedFiles(uploadedCopy);

                toast.success("Certificate deleted successfully");
            }
            else {
                toast.error(result.message || "Failed to delete certificate");
            }
        } catch (err) {
            toast.error("Server error during certificate deletion");
        } finally {
            setLoading(false); // End loading
            setIsDeleteConfirmOpen(false);
            setFileToDeleteIndex(null);
        }
    };


    const handleDownloadExcel = () => {
        if (filteredCards.length === 0) {
            toast.info("No records found to download.");
            return;
        }

        if (!startDate && !endDate) {
            // Store the data to download after confirmation
            setPendingDownloadData(filteredCards);
            setIsDownloadConfirmOpen(true);
            return;
        }
        downloadExcel(filteredCards);


        // const formattedData = filteredCards.map((card) => {
        //     const m = card.manuscriptDetails;
        //     return {
        //         Date: new Date(m.createdAt).toLocaleString(),
        //         ReferenceNumber: m.referenceNumber,
        //         Name: m.name,
        //         Email: m.email,
        //         Phone: m.phone,
        //         Country: m.country,
        //         Address: m.address,
        //         ArticleTitle: m.articleTitle,
        //         ArticleCategory: m.articleCategory,
        //         Status: m.statusOptions,
        //         PaymentStatus: m.pay_status,
        //         Amount: card.fee,
        //         File: m.file ? `https://releasesworld.100billionoms.com/uploads/manuscripts/${m.file}` : "",
        //         Certificate: m.certificate ? `https://releasesworld.100billionoms.com/uploads/manuscripts/${m.certificate}` : "",
        //     };
        // });

        // const worksheet = XLSX.utils.json_to_sheet(formattedData);
        // const workbook = XLSX.utils.book_new();
        // XLSX.utils.book_append_sheet(workbook, worksheet, "Manuscripts");

        // const excelBuffer = XLSX.write(workbook, {
        //     bookType: "xlsx",
        //     type: "array",
        // });

        // const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        // saveAs(blob, "manuscripts_export.xlsx");
    };

    const downloadExcel = (dataToExport) => {
        const formattedData = dataToExport.map((card) => {
            const m = card.manuscriptDetails;
            return {
                Date: new Date(m.createdAt).toLocaleString(),
                ReferenceNumber: m.referenceNumber,
                Name: m.name,
                Email: m.email,
                Phone: m.phone,
                Country: m.country,
                Address: m.address,
                ArticleTitle: m.articleTitle,
                ArticleCategory: m.articleCategory,
                Status: m.statusOptions,
                PaymentStatus: m.pay_status,
                Amount: card.fee,
                File: m.file ? `https://releasesworld.100billionoms.com/uploads/manuscripts/${m.file}` : "",
                Certificate: m.certificate ? `https://releasesworld.100billionoms.com/uploads/manuscripts/${m.certificate}` : "",
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Manuscripts");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, "manuscripts_export.xlsx");
        toast.success("Excel downloaded!");
    };

    const fetchDataPlan = async () => {
        try {
            const response = await fetch(`https://releasesworld.100billionoms.com/volume`);
            const res = await response.json();

            // assuming res looks like [{id: 1, name: "Vol 1"}, {id: 2, name: "Vol 2"}]
            const options = res.map(v => ({
                value: v.id,      // store volume_id
                label: v.volume_name     // display volume name
            }));

            setVolumeOptions(options);
        } catch (error) {
            console.log(error);
        }
    };

    const fetchDataIssue = async () => {
        try {
            const response = await fetch(`https://releasesworld.100billionoms.com/issue`);
            const res = await response.json();

            // assuming res looks like [{id: 1, name: "Vol 1"}, {id: 2, name: "Vol 2"}]
            const options = res.map(v => ({
                value: v.id,      // store volume_id
                label: v.issue     // display volume name
            }));

            setIssueOptions(options);
        } catch (error) {
            console.log(error);
        }
    };


    useEffect(() => {
        fetchDataPlan();
        fetchDataIssue();
    }, [])


    return (
        <>
            <div className={`open-close ${isSidebarOpen ? 'open' : 'closed'}`}>
                <div className="articles-impact">
                    <h1 className="dashboard-title">Articles</h1>

                    <div className="filter-search" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="filter-search-input" style={{ flex: 1, marginRight: '16px' }}>
                            <input
                                type="search"
                                placeholder="Search by title, name, category, ref no, status..."
                                value={searchQuery}
                                onChange={handleSearch}
                            />
                            <i className="fa fa-search"></i>
                        </div>

                        <div className="filter-payment-dropdown ">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                />
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                                />
                                <button className='oms-add-btn mainbuttoncheck' onClick={handleDownloadExcel}><i
                                    className="fa fa-download"></i> Excel</button>
                            </div>


                            <select
                                value={paymentFilter}
                                onChange={(e) => setPaymentFilter(e.target.value)}
                                style={{
                                    padding: '8px 12px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    fontSize: '14px'
                                }}
                            >
                                <option value="All">All</option>
                                <option value="Paid">Paid</option>
                                <option value="Unpaid">Unpaid</option>
                            </select>
                        </div>
                    </div>

                    <div className="articles-impact-cards">
                        {currentCards.map((card, index) => {
                            const globalIndex = indexOfFirstCard + index;
                            return (
                                <div
                                    key={globalIndex}
                                    className={`article-impact-card ${(card.manuscriptDetails.pay_status || '').toLowerCase() === 'paid' ? 'paid' : 'unpaid'}`}


                                >
                                    <div className="article-impact-card-header">
                                        <p>{card.manuscriptDetails.referenceNumber}</p>
                                        <p className={`payment-status ${(card.manuscriptDetails.pay_status || '').toLowerCase()}`}>
                                            {card.manuscriptDetails.pay_status || 'unpaid'} - ₹{card.fee}
                                        </p>

                                    </div>
                                    <div className="article-impact-card-body">
                                        <div>
                                            <div className="article-impact-body-header">
                                                <div className="article-impact-body-category">
                                                    <p>{card.manuscriptDetails.articleCategory}</p>
                                                </div>
                                                <div className="article-impact-body-header-icons">
                                                    <i
                                                        className="fa fa-download"
                                                        title="Download File"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            const fileUrl = `https://releasesworld.100billionoms.com/uploads/manuscripts/${card.manuscriptDetails.file}`;
                                                            const link = document.createElement('a');
                                                            link.href = fileUrl;
                                                            link.target = "_blank";
                                                            link.download = card.file;
                                                            document.body.appendChild(link);
                                                            link.click();
                                                            document.body.removeChild(link);
                                                        }}
                                                    ></i>

                                                    <i className="fa fa-phone" title={`Call ${card.manuscriptDetails.phone}`} style={{ cursor: 'pointer' }} onClick={() => window.location.href = `tel:${card.phone}`}></i>
                                                    <i className="fa fa-envelope" title={`Email ${card.manuscriptDetails.email}`} style={{ cursor: 'pointer' }} onClick={() => window.location.href = `mailto:${card.email}`}></i>
                                                    <i className="fa fa-eye" title="View Details" style={{ cursor: 'pointer' }} onClick={() => handleEyeClick(card)}></i>
                                                </div>
                                            </div>
                                            <div className="article-impact-body-title">
                                                <h2>{card.manuscriptDetails.articleTitle}</h2>
                                            </div>
                                            <h4>{card.name}</h4>
                                            <div className="article-impact-body-address">
                                                <p>{card.manuscriptDetails.address}</p>
                                            </div>
                                        </div>
                                        <div className="article-impact-card-footer">
                                            <select
                                                className={`status-dropdown ${getStatusClass(card.manuscriptDetails.statusOptions || 'Under Review')}`}
                                                value={card.manuscriptDetails.statusOptions || 'Under Review'}
                                                onChange={(e) => handleStatusChange(e, card)}  // ✅ pass full card
                                            >

                                                {statusOptions.map((status) => (
                                                    <option key={status} value={status}>{status}</option>
                                                ))}
                                            </select>


                                            {(card.manuscriptDetails.statusOptions || 'Under Review') === 'Published' && (
                                                <div className="certificate-upload-wrapper">

                                                    {/* Upload button (shown only if no file in DB or temp state) */}
                                                    {!card.manuscriptDetails.certificate && !uploadedFiles[globalIndex] && (
                                                        <button
                                                            className="upload-certificate-btn"
                                                            onClick={() => document.getElementById(`upload-input-${globalIndex}`).click()}
                                                            disabled={loading} // Disable button while loading
                                                        >
                                                            {loading ? (
                                                                <span className="spinner"></span> // Show spinner
                                                            ) : (
                                                                "Upload Certificate"
                                                            )}
                                                        </button>
                                                    )}


                                                    <input
                                                        type="file"
                                                        id={`upload-input-${globalIndex}`}
                                                        style={{ display: 'none' }}
                                                        accept="application/pdf"
                                                        onChange={(e) => {
                                                            const file = e.target.files[0];
                                                            if (file) {
                                                                const formData = new FormData();
                                                                formData.append("certificate", file);

                                                                fetch(`https://releasesworld.100billionoms.com/manuscript/upload-certificate/${card.manuscriptDetails.referenceNumber}`, {
                                                                    method: "POST",
                                                                    body: formData,
                                                                })
                                                                    .then(async (res) => {
                                                                        const result = await res.json();
                                                                        if (res.ok) {
                                                                            toast.success("Certificate uploaded successfully");
                                                                            const updated = [...cards];
                                                                            updated[globalIndex] = {
                                                                                ...updated[globalIndex],
                                                                                manuscriptDetails: {
                                                                                    ...updated[globalIndex].manuscriptDetails,
                                                                                    certificate: result.file
                                                                                }
                                                                            };
                                                                            setCards(updated);

                                                                        } else {
                                                                            toast.error(result.message || "Upload failed");
                                                                        }
                                                                    })
                                                                    .catch(() => {
                                                                        toast.error("Server error while uploading certificate");
                                                                    });
                                                            }
                                                        }}
                                                    />

                                                    {/* Show uploaded file info from backend or local state */}
                                                    {(card.manuscriptDetails.certificate || uploadedFiles[globalIndex]) && (
                                                        <div className="uploaded-file-info">
                                                            <span
                                                                className="file-name"
                                                                onClick={() => {
                                                                    const fileName = card.manuscriptDetails.certificate || uploadedFiles[globalIndex]?.name;
                                                                    const fileUrl = card.manuscriptDetails.certificate
                                                                        ? `https://releasesworld.100billionoms.com/uploads/manuscripts/${card.manuscriptDetails.certificate}`
                                                                        : URL.createObjectURL(uploadedFiles[globalIndex].file);
                                                                    const link = document.createElement('a');
                                                                    link.href = fileUrl;
                                                                    link.download = fileName;
                                                                    link.target = '_blank';
                                                                    link.click();
                                                                }}
                                                            >
                                                                {(card.manuscriptDetails.certificate || uploadedFiles[globalIndex]?.name).slice(0, 20)}...
                                                            </span>
                                                            <Trash2
                                                                size={12}
                                                                color="red"
                                                                className="delete-icon"
                                                                onClick={() => {
                                                                    setSelectedArticle(card);
                                                                    setFileToDeleteIndex(globalIndex);
                                                                    setIsDeleteConfirmOpen(true);
                                                                }}
                                                            />
                                                        </div>
                                                    )}

                                                </div>
                                            )}
                                        </div>

                                        <div className="article-impact-card-footer">
                                            {/* New Volume Dropdown (React Select) */}
                                            <Select
                                                className="react-select-dropdown"
                                                options={volumeOptions}
                                                value={
                                                    volumeOptions.find(opt => opt.value === card.manuscriptDetails.volume_id) ||
                                                    volumeOptions.find(opt => opt.label === card.manuscriptDetails.volume) ||
                                                    null
                                                }
                                                onChange={(selectedOption) => {
                                                    const updated = [...cards];
                                                    updated[globalIndex] = {
                                                        ...updated[globalIndex],
                                                        manuscriptDetails: {
                                                            ...updated[globalIndex].manuscriptDetails,
                                                            volume_id: selectedOption.value,   // ✅ always store id
                                                            volume: selectedOption.label       // ✅ optional: keep string for display
                                                        },
                                                    };
                                                    setCards(updated);

                                                    // send update immediately
                                                    fetch(`https://releasesworld.100billionoms.com/manuscript/updatearticle/${card.manuscriptDetails.id}`, {
                                                        method: "PUT",
                                                        headers: { "Content-Type": "application/json" },
                                                        body: JSON.stringify({ volume_id: selectedOption.value }),
                                                    });
                                                }}
                                                placeholder="Select Volume"
                                            />


                                            <Select
                                                className="react-select-dropdown"
                                                options={issueOptions}
                                                value={
                                                    issueOptions.find(opt => opt.value === card.manuscriptDetails.issue_id) ||
                                                    issueOptions.find(opt => opt.label === card.manuscriptDetails.issue) ||
                                                    null
                                                }
                                                onChange={(selectedOption) => {
                                                    const updated = [...cards];
                                                    updated[globalIndex] = {
                                                        ...updated[globalIndex],
                                                        manuscriptDetails: {
                                                            ...updated[globalIndex].manuscriptDetails,
                                                            issue_id: selectedOption.value,
                                                            issue: selectedOption.label
                                                        },
                                                    };
                                                    setCards(updated);

                                                    fetch(`https://releasesworld.100billionoms.com/manuscript/updatearticle/${card.manuscriptDetails.id}`, {
                                                        method: "PUT",
                                                        headers: { "Content-Type": "application/json" },
                                                        body: JSON.stringify({ issue_id: selectedOption.value }),
                                                    });
                                                }}
                                                placeholder="Select Issue"
                                            />

                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="pagination">
                        <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                            <i class="fa-solid fa-chevron-left"></i><i class="fa-solid fa-chevron-left"></i>
                        </button>



                        <button className="active">
                            {currentPage}
                        </button>

                        {currentPage < totalPages && (
                            <button onClick={() => paginate(currentPage + 1)}>
                                {currentPage + 1}
                            </button>
                        )}

                        {currentPage + 1 < totalPages && (
                            <span className="dots">...</span>
                        )}

                        {currentPage !== totalPages && (
                            <button onClick={() => paginate(totalPages)}>
                                {totalPages}
                            </button>
                        )}

                        <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                            <i class="fa-solid fa-chevron-right"></i><i class="fa-solid fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && selectedArticle && (
                <div className={`all-article-modal-overlay-impact all-article-${selectedArticle.manuscriptDetails.articleTitle.replace(/\s+/g, '-').toLowerCase()}-impact`}>
                    <div className="all-article-modal-content-impact">
                        <button className="all-article-close-modal-impact" onClick={closeModal}>×</button>
                        <h2>Article Details</h2>
                        <ul>
                            <li><strong>Reference Number:</strong> {selectedArticle.manuscriptDetails.referenceNumber}</li>
                            <li><strong>Title:</strong> {selectedArticle.title || selectedArticle.manuscriptDetails.articleTitle}</li>
                            <li><strong>Author:</strong> {selectedArticle.author || selectedArticle.manuscriptDetails.name}</li>
                            <li><strong>Category:</strong> {selectedArticle.category || selectedArticle.manuscriptDetails.articleCategory}</li>
                            <li><strong>Amount:</strong> ₹ {selectedArticle.fee}</li>
                            <li><strong>Payment Status:</strong> {selectedArticle.manuscriptDetails.pay_status}</li>
                            <li><strong>Address:</strong> {selectedArticle.manuscriptDetails.address}</li>
                            <li><strong>Email:</strong> {selectedArticle.manuscriptDetails.email}</li>
                            <li><strong>Mobile:</strong> {selectedArticle.manuscriptDetails.phone}</li>
                            <li><strong>Download File:</strong> {selectedArticle.manuscriptDetails.file.split('/').pop()}</li>
                        </ul>
                    </div>
                </div>
            )}


            {isDeleteConfirmOpen && (
                <div className="account-modal-backdrop">
                    <div className="account-modal-content">
                        <h3>Confirm Certificate Delete</h3>
                        <p>Are you sure you want to delete this file?</p>
                        <div className="account-modal-buttons">
                            <button className="account-confirm-btn" onClick={confirmFileDelete}>Yes, Delete</button>
                            <button className="account-cancel-btn" onClick={cancelFileDelete}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            {isDownloadConfirmOpen && (
                <div className="account-modal-backdrop">
                    <div className="account-modal-content">
                        <h3>Confirm Excel Download</h3>
                        <p>Do you want to download <strong>all</strong> records?</p>
                        <div className="account-modal-buttons">
                            <button
                                className="account-confirm-btn"
                                onClick={() => {
                                    if (pendingDownloadData) {
                                        downloadExcel(pendingDownloadData);
                                    }
                                    setIsDownloadConfirmOpen(false);
                                    setPendingDownloadData(null);
                                }}
                            >
                                Yes, Download
                            </button>

                            <button
                                className="account-cancel-btn"
                                onClick={() => {
                                    setIsDownloadConfirmOpen(false);
                                    setPendingDownloadData(null);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={confirmStatusChange}
                newStatus={pendingStatus}
            />


        </>
    );
};

export default Articles;

const ConfirmationModal = ({ isOpen, onClose, onConfirm, newStatus }) => {
    if (!isOpen) return null;

    return (
        <div className="account-modal-backdrop">
            <div className="account-modal-content">
                <h3>Confirm Status Change</h3>
                <p>Are you sure you want to change the status to <strong>{newStatus}</strong>?</p>
                <div className="account-modal-buttons">
                    <button className="account-confirm-btn" onClick={onConfirm}>Yes, Change</button>
                    <button className="account-cancel-btn" onClick={onClose}>Cancel</button>
                </div>
            </div>
        </div>
    );
};